/**
 * Utility functions per la gestione degli URL nel flusso SSO
 */

/**
 * Determina lo schema (http/https) da usare per un URL basandosi sul dominio.
 * 
 * @param urlApp L'URL o dominio dell'applicazione
 * @returns 'http' per localhost/IP privati, 'https' per altri domini
 */
export function defaultSchemeFor(urlApp: string): 'http' | 'https' {
  const v = (urlApp ?? '').toLowerCase();
  if (v.startsWith('localhost') || v.startsWith('127.0.0.1') || v.startsWith('10.') || v.startsWith('192.168.') || v.startsWith('172.16.')) {
    return 'http';
  }
  if (/^\d{1,3}(\.\d{1,3}){3}(:\d+)?/.test(v)) {
    return 'http';
  }
  return 'https';
}

const SSO_CALLER_DEV_KEY = 'sso-caller-dev';
const SSO_CALLER_ORIGIN_KEY = 'sso-caller-origin';
const LANDING_PAGE_PATH = '/landing-page';

/**
 * True se l'hostname è un host di sviluppo locale.
 */
export function isLocalhostHost(hostname: string): boolean {
  const host = (hostname ?? '').toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
}

/**
 * True se `url` ha hostname locale. Restituisce false se l'URL non è parsabile.
 */
export function isLocalhostUrl(url: string): boolean {
  try {
    return isLocalhostHost(new URL(url).hostname);
  } catch {
    return false;
  }
}

export function getSsoCallerOrigin(): string | null {
  try {
    return sessionStorage.getItem(SSO_CALLER_ORIGIN_KEY);
  } catch {
    return null;
  }
}

export function isSsoCallerOnLocalhost(): boolean {
  try {
    if (sessionStorage.getItem(SSO_CALLER_DEV_KEY) === '1') {
      return true;
    }
  } catch {
    /* ignore */
  }
  const origin = getSsoCallerOrigin();
  return !!origin && isLocalhostUrl(origin);
}

export function clearSsoCallerDevFlag(): void {
  try {
    sessionStorage.removeItem(SSO_CALLER_DEV_KEY);
    sessionStorage.removeItem(SSO_CALLER_ORIGIN_KEY);
  } catch {
    /* sessionStorage non disponibile */
  }
}

/**
 * All'ingresso: persiste se il chiamante è locale (query `dev` / `return_origin`
 * o document.referrer). HTTP→HTTPS spesso svuota il referrer.
 * Su /landing-page il flag viene azzerato.
 */
export function captureSsoCallerDevFromReferrer(): void {
  try {
    const currentPath = window.location.pathname || '/';
    if (currentPath === LANDING_PAGE_PATH) {
      clearSsoCallerDevFlag();
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const returnOrigin = params.get('return_origin');
    const devParam = params.get('dev');
    if (returnOrigin && isLocalhostUrl(returnOrigin)) {
      sessionStorage.setItem(SSO_CALLER_ORIGIN_KEY, new URL(returnOrigin).origin);
      sessionStorage.setItem(SSO_CALLER_DEV_KEY, '1');
    }
    if (devParam === 'true' || devParam === '1') {
      sessionStorage.setItem(SSO_CALLER_DEV_KEY, '1');
    }

    const referrer = document.referrer;
    if (!referrer) {
      return;
    }

    const referrerUrl = new URL(referrer);
    if (referrerUrl.origin === window.location.origin) {
      return;
    }

    if (isLocalhostHost(referrerUrl.hostname)) {
      sessionStorage.setItem(SSO_CALLER_DEV_KEY, '1');
      sessionStorage.setItem(SSO_CALLER_ORIGIN_KEY, referrerUrl.origin);
    } else if (!getSsoCallerOrigin() && sessionStorage.getItem(SSO_CALLER_DEV_KEY) !== '1') {
      clearSsoCallerDevFlag();
    }
  } catch {
    /* referrer o sessionStorage non utilizzabili */
  }
}

/**
 * Costruisce l'URL di redirect SSO aggiungendo ticket e id_profilo.
 * Il flusso generate-ticket usa `redirect_url` dell'API; questa utility resta
 * per casi in cui serve assemblare l'URL lato client.
 */
export function buildRedirectUrl(urlApp: string, ticket: string, idProfilo: number): string {
  const trimmed = (urlApp ?? '').trim();
  const hasScheme = trimmed.startsWith('http://') || trimmed.startsWith('https://');
  const scheme = defaultSchemeFor(trimmed);
  const base = hasScheme ? trimmed : `${scheme}://${trimmed}`;
  const url = new URL(base);
  url.searchParams.set('ticket', ticket);
  url.searchParams.set('id_profilo', String(idProfilo));
  return url.toString();
}
